<script setup lang="ts">
import axios from 'axios'

import type { AudienceRule } from '@/types/types'
import type { ContestGame } from '@/types/global'

type AudienceContestClasses = {
  colGrid: string
  label: string
  checkboxesContainer: string
}

type ContestGamesResponse = {
  data: Array<{
    id: number | string
    name: string
  }>
  meta?: {
    last_page?: number
  }
}

const props = defineProps<{
  rule: AudienceRule
  disabled: boolean
  classes: AudienceContestClasses
  isHighlighted?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:rule', value: AudienceRule): void
  (e: 'update:games', value: unknown): void
}>()

const { t } = useI18n()

const contestGames = ref<ContestGame[]>([])
const selectedContestGameIds = ref<number[]>([])

const isInitialLoading = ref(false)
const isFetchingContestGames = ref(false)
const contestGamesLoaded = ref(false)

const selectedContestGames = computed(() =>
  contestGames.value.filter((game) => selectedContestGameIds.value.includes(Number(game.id)))
)

const addGames = (newGames: ContestGamesResponse['data']) => {
  const merged = new Map(contestGames.value.map((game) => [game.id, game]))
  for (const game of newGames) {
    const normalizedId = String(game.id)
    merged.set(normalizedId, { id: normalizedId, value: normalizedId, text: game.name })
  }
  contestGames.value = Array.from(merged.values())
}

const fetchSelectedContestGames = async (ids: number[]) => {
  if (!ids.length) return
  try {
    const { data } = await axios.get<ContestGamesResponse>('/contest-games', {
      params: { ids }
    })
    addGames(data.data ?? [])
  } catch (error) {
    throw new Error(`Error fetching selected contest games: ${error}`)
  }
}

const CONTEST_GAMES_PAGE_SIZE = 50

const fetchAllContestGames = async () => {
  if (contestGamesLoaded.value || isFetchingContestGames.value) return
  isFetchingContestGames.value = true
  try {
    let page = 1
    let lastPage = 1

    do {
      const { data } = await axios.get<ContestGamesResponse>('/contest-games', {
        params: { page, per_page: CONTEST_GAMES_PAGE_SIZE }
      })
      addGames(data.data ?? [])
      lastPage = data.meta?.last_page ?? page
      page += 1
    } while (page <= lastPage)

    contestGamesLoaded.value = true
  } catch (error) {
    throw new Error(`Error fetching contest games: ${error}`)
  } finally {
    isFetchingContestGames.value = false
  }
}

const onOpenContestGames = async () => {
  await fetchAllContestGames()
}

const uniqueGameOptions = computed(() =>
  contestGames.value.map((game) => ({
    value: Number(game.id),
    label: game.text,
    disabled: false
  }))
)

const updateContestGameRules = () => {
  const updatedRule = { ...props.rule, value: selectedContestGameIds.value }
  emit('update:rule', updatedRule)
  emit('update:games', selectedContestGames.value)
}

onMounted(async () => {
  isInitialLoading.value = true
  try {
    const selectedIds = (props.rule.value as number[]) ?? []
    if (selectedIds.length > 0) {
      await fetchSelectedContestGames(selectedIds)
      selectedContestGameIds.value = selectedIds
      emit('update:games', selectedContestGames.value)
    }
  } finally {
    isInitialLoading.value = false
  }
})
</script>

<template>
  <div
    v-if="!isInitialLoading"
    :class="[
      classes.colGrid,
      {
        'bg-blue-50/30 rounded-lg p-3 ring-1 ring-blue-600': isHighlighted
      }
    ]"
  >
    <label :class="classes.label">
      {{ t('pages.audience.audienceCreatePage.block.contestGame.title', selectedContestGameIds.length > 1 ? 2 : 1) }}
    </label>
    <div :class="classes.checkboxesContainer">
      <BaseMultiSelect
        v-model="selectedContestGameIds"
        :disabled="disabled"
        :loading="isFetchingContestGames"
        :options="uniqueGameOptions"
        :filter="true"
        :filter-match-mode="'contains'"
        :placeholder="t('global.multiselect.gameContest.placeholder')"
        :filter-placeholder="t('global.multiselect.gameContest.filterPlaceholder')"
        :empty-filter-message="t('global.multiselect.gameContest.emptyFilterMessage')"
        :selected-items-label="
          t('global.multiselect.gameContest.selectedItems', {
            count: selectedContestGameIds.length
          })
        "
        @update:model-value="updateContestGameRules"
        @show="onOpenContestGames"
      />
    </div>
  </div>
  <AppSkeleton v-else type="section" class="overflow-hidden rounded-lg border" />
</template>
